/* eslint-disable quotes */
import { useEffect } from "react";

/**
 * Custom hook for managing SEO meta tags without external dependencies
 * @param {Object} seoConfig - Configuration object for SEO
 * @param {string} seoConfig.title - Page title
 * @param {string} seoConfig.description - Page description
 * @param {string} [seoConfig.keywords] - Meta keywords
 * @param {string} [seoConfig.ogTitle] - Open Graph title (defaults to title)
 * @param {string} [seoConfig.ogDescription] - Open Graph description (defaults to description)
 * @param {string} [seoConfig.ogImage] - Open Graph image URL
 * @param {string} [seoConfig.ogUrl] - Open Graph URL (defaults to current URL)
 * @param {string} [seoConfig.ogType] - Open Graph type (defaults to 'website')
 * @param {string} [seoConfig.twitterCard] - Twitter card type (defaults to 'summary_large_image')
 * @param {string} [seoConfig.canonical] - Canonical URL
 */
const useSEO = (seoConfig) => {
  useEffect(() => {
    if (!seoConfig) return;

    const {
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogImage,
      ogUrl,
      ogType = "website",
      twitterCard = "summary_large_image",
      canonical,
    } = seoConfig;

    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, content, attribute = "content") => {
      if (!content) return;

      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        const selectorParts = selector.match(/\[(.+?)=["'](.+?)["']\]/);
        if (selectorParts) {
          element.setAttribute(selectorParts[1], selectorParts[2]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Helper function to update or create link tag
    const updateLinkTag = (rel, href) => {
      if (!href) return;

      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Basic meta tags
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);

    // Open Graph tags
    updateMetaTag('meta[property="og:title"]', ogTitle || title);
    updateMetaTag(
      'meta[property="og:description"]',
      ogDescription || description,
    );
    updateMetaTag('meta[property="og:image"]', ogImage);
    updateMetaTag('meta[property="og:url"]', ogUrl || window.location.href);
    updateMetaTag('meta[property="og:type"]', ogType);
    updateMetaTag('meta[property="og:site_name"]', "FindASide");

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', twitterCard);
    updateMetaTag('meta[name="twitter:title"]', ogTitle || title);
    updateMetaTag(
      'meta[name="twitter:description"]',
      ogDescription || description,
    );
    updateMetaTag('meta[name="twitter:image"]', ogImage);

    // Canonical URL
    updateLinkTag("canonical", canonical || window.location.href);

    // Cleanup function to restore defaults
    return () => {
      document.title = "FindASide";
    };
  }, [seoConfig]);
};

export default useSEO;
